import { ONNX_RUNTIME_WEB_VERSION } from './freecutVersion';
import type { FreecutProject } from './studioStart';
import type { StudioRenderRequest } from './studioRenderRequest';

/** Options of FreeCut's `mountFreecutEditor` that the studio uses. */
export interface FreecutEditorOptions {
  project?: FreecutProject;
  language?: 'en' | 'pt-BR';
  singleProject?: boolean;
  onSave?: (project: FreecutProject) => void;
  onExport?: (blob: Blob, name: string) => void | Promise<void>;
  onClose?: () => void;
  aiRuntimeUrl?: string;
}

export interface FreecutEditorHandle {
  ready: Promise<void>;
  getProject: () => FreecutProject | null;
  unmount: () => Promise<void>;
}

/** The vendored editor bundle (public/freecut/, see scripts/sync-freecut-editor.mjs). */
export interface FreecutEditorModule {
  mountFreecutEditor: (element: HTMLElement, options?: FreecutEditorOptions) => FreecutEditorHandle;
  /** Imports a Remotion render request (media probed over the network) as a FreeCut project. */
  importAutoclipperRender: (
    request: StudioRenderRequest,
  ) => Promise<{ project: FreecutProject; duration: number; warnings: string[] }>;
  SUPPORTED_AUTOCLIPPER_COMPOSITIONS: readonly string[];
}

/** Served from public/ and imported at runtime: Vite must not bundle it (it ships its own React). */
const EDITOR_URL = '/freecut/freecut-editor.js';

/** Imported by absolute URL: Vite's dev server adds `?import` to a root-relative dynamic import,
 *  and refuses to serve a public/ file with that query. */
export const loadFreecutEditor = (): Promise<FreecutEditorModule> =>
  import(/* @vite-ignore */ new URL(EDITOR_URL, window.location.origin).href) as Promise<FreecutEditorModule>;

/** jsDelivr copy of the ONNX Runtime build the editor's AI features expect (not vendored). */
export const FREECUT_AI_RUNTIME_URL = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ONNX_RUNTIME_WEB_VERSION}/dist/`;

/** FreeCut's language for autoclipper's (`pt` | `en`). */
export const freecutLanguage = (language: string): 'en' | 'pt-BR' =>
  language.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en';
